/**
 * Aparência de uma estação de rio no mapa — funções puras (testadas em
 * `look.test.ts`).
 *
 * **A cor é a posição percentílica, nunca a cota.** As réguas têm zeros
 * arbitrários: colorir por centímetro pintaria Manaus (2069 cm) e Óbidos
 * (319 cm) como se fossem coisas comparáveis, e elas não são. O percentil do
 * dia do ano é adimensional e responde à pergunta certa — "está alto ou baixo
 * PARA ESTA ÉPOCA, NESTE RIO?".
 *
 * A escala é divergente e tem um eixo só: seca ↔ cheia. O marrom não é uma cor
 * escolhida por gosto — é a cor do rio vazado na vazante, e é a que a imprensa
 * amazônica usa nas fotos de seca.
 *
 * Estação **sem referência** (menos de 8 anos medidos) não recebe cor: fica
 * vazada e tracejada. Tratá-la como "normal" apagaria justamente as estações
 * sobre as quais não sabemos nada — o mesmo erro que `SEM_DADO` evita nas
 * estações de chuva.
 */
import type { Classe, Tendencia } from './types';

/**
 * Escala divergente, do mais seco ao mais cheio.
 *
 * Os tons foram clareados depois de medir o resultado no mapa: a primeira
 * versão usava marrons escuros que, num ponto de 5 px sobre a imagem noturna
 * do GOES, sumiam. Sobre fundo escuro, quem carrega a informação é a
 * LUMINOSIDADE, não a saturação.
 *
 * O extremo seco é ocre, e não laranja: laranja já é foco de queimada neste
 * mapa, e dois significados na mesma tinta não se leem.
 */
export const COR_CLASSE: Readonly<Record<Classe, string>> = {
  muitoAbaixo: '#e0973f',
  abaixo: '#e8c48a',
  normal: '#f0e8d8',
  acima: '#8ecae6',
  muitoAcima: '#4d97c9',
};

/** Contorno das estações sem referência histórica. */
export const COR_SEM_REFERENCIA = '#8c99ab';

export type Estado = 'comCor' | 'semReferencia' | 'semDadoNoQuadro';

export interface Visual {
  estado: Estado;
  classe: Classe | null;
  /** Raio do círculo em px. */
  raio: number;
  preenchimento: string | null;
  contorno: string;
  larguraContorno: number;
  tracejado: readonly number[];
  opacidade: number;
}

export function visualDe(
  classe: Classe | null,
  temCota: boolean,
  tamanho: number,
  opacidade: number,
): Visual {
  // Quadro fora da janela publicada: não afirmamos cota nenhuma.
  if (!temCota) {
    return {
      estado: 'semDadoNoQuadro',
      classe: null,
      raio: tamanho * 0.8,
      preenchimento: null,
      contorno: COR_SEM_REFERENCIA,
      larguraContorno: 1.2,
      tracejado: [2, 2],
      opacidade: opacidade * 0.55,
    };
  }

  if (classe === null) {
    // Menor e mais apagada que as coloridas, de propósito: são 49 de 154, e
    // se tiverem o mesmo peso visual o mapa passa a ser dominado pelo que NÃO
    // sabemos, em vez de pelo que medimos.
    return {
      estado: 'semReferencia',
      classe: null,
      raio: tamanho * 0.6,
      preenchimento: null,
      contorno: COR_SEM_REFERENCIA,
      larguraContorno: 1.1,
      tracejado: [2, 2],
      opacidade: opacidade * 0.65,
    };
  }

  return {
    estado: 'comCor',
    classe,
    raio: tamanho,
    preenchimento: COR_CLASSE[classe],
    // Contorno escuro e opaco: sem ele, um ponto claro sobre nuvem branca
    // desaparece, e é justamente sobre nuvem que o mapa fica mais claro.
    contorno: 'rgba(8,13,24,0.9)',
    larguraContorno: 1.2,
    tracejado: [],
    opacidade,
  };
}

/**
 * Chave do cache de estilos.
 *
 * O chevron da tendência é desenhado como TEXTO ao lado do círculo, e não
 * gravado no sprite: assim o cache fica em `classes × estados`, em vez de
 * multiplicar por tendência e por valor. Com 5 classes mais dois estados sem
 * cor, são 7 aparências — e o teste trava esse número.
 */
export function chaveEstilo(v: Visual, dpr: number): string {
  return [dpr.toFixed(2), v.estado, v.classe ?? '-', v.raio.toFixed(2), v.opacidade.toFixed(3)].join('|');
}

/** Cor do chevron: legível sobre imagem clara e escura. */
export const COR_TENDENCIA: Readonly<Record<Tendencia, string>> = {
  subindo: '#7fe3ff',
  descendo: '#ffb27f',
  estavel: '#c3ccda',
  desconhecida: 'transparent',
};

/** Ordem das classes na legenda, do mais seco ao mais cheio. */
export const CLASSES: readonly Classe[] = ['muitoAbaixo', 'abaixo', 'normal', 'acima', 'muitoAcima'];
